import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { fetchAdminAiDebug } from "../api/aiIntelligence";

function formatDate(value) {
  if (!value) return "";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

function metadataSummary(metadata) {
  if (!metadata || typeof metadata !== "object") return "";
  return Object.entries(metadata)
    .filter(([, value]) => value !== "" && value != null)
    .slice(0, 5)
    .map(([key, value]) => `${key}: ${String(value)}`)
    .join(" | ");
}

function compactText(value) {
  if (!value) return "";
  const text = typeof value === "string" ? value : JSON.stringify(value, null, 2);
  return text.length > 900 ? `${text.slice(0, 900)}...` : text;
}

export default function AdminAiDebugPage() {
  const [events, setEvents] = useState([]);
  const [llmMessages, setLlmMessages] = useState([]);
  const [appliedStats, setAppliedStats] = useState(null);
  const [appliedChanges, setAppliedChanges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  async function load() {
    setLoading(true);
    setError("");
    try {
      const payload = await fetchAdminAiDebug();
      setEvents(Array.isArray(payload?.events) ? payload.events : []);
      setLlmMessages(Array.isArray(payload?.llm_messages) ? payload.llm_messages : []);
      setAppliedStats(payload?.applied_change_stats || null);
      setAppliedChanges(Array.isArray(payload?.applied_changes) ? payload.applied_changes : []);
    } catch (err) {
      setError(err?.message || "Unable to load AI debug events.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  const counts = useMemo(() => {
    return events.reduce((acc, event) => {
      acc[event.event_type] = (acc[event.event_type] || 0) + 1;
      return acc;
    }, {});
  }, [events]);

  return (
    <Box sx={{ maxWidth: 1180, mx: "auto", p: { xs: 2, md: 3 } }}>
      <Stack spacing={2.2}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 900, color: "#15243b" }}>
              AI Debug
            </Typography>
            <Typography variant="body2" sx={{ color: "#667085", mt: 0.5 }}>
              Recent Insights calls, cache events, fallbacks, and user feedback.
            </Typography>
          </Box>
          <Button variant="outlined" onClick={load} disabled={loading} sx={{ textTransform: "none" }}>
            {loading ? "Refreshing..." : "Refresh"}
          </Button>
        </Stack>

        {error ? <Alert severity="error">{error}</Alert> : null}

        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
          <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", rowGap: 1 }}>
            {Object.keys(counts).length ? (
              Object.entries(counts).map(([eventType, count]) => (
                <Chip key={eventType} label={`${eventType}: ${count}`} />
              ))
            ) : (
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                No AI events found.
              </Typography>
            )}
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Stack spacing={0.5}>
            <Typography variant="h6" sx={{ fontWeight: 900, color: "#15243b" }}>
              LLM message audit
            </Typography>
            <Typography variant="body2" sx={{ color: "#667085" }}>
              Shows who the model response was for, which page used it, what context was sent when stored, and what text/JSON came back.
            </Typography>
          </Stack>
        </Paper>

        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Time</TableCell>
                <TableCell>User / recipient</TableCell>
                <TableCell>Page</TableCell>
                <TableCell>Task</TableCell>
                <TableCell>Sent to LLM</TableCell>
                <TableCell>Returned / shown</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {llmMessages.map((message) => (
                <TableRow key={message.id} hover sx={{ verticalAlign: "top" }}>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{formatDate(message.created_at)}</TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {message.username || message.recipient || `User ${message.user_id}`}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      ID {message.user_id}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={message.page || "Unknown"} />
                  </TableCell>
                  <TableCell sx={{ minWidth: 150 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {message.task_type || "AI task"}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
                      {message.model || message.source}
                    </Typography>
                    {message.latency_ms ? (
                      <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
                        {message.latency_ms}ms
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell sx={{ minWidth: 260, maxWidth: 360 }}>
                    {message.prompt_summary ? (
                      <Typography
                        component="pre"
                        variant="caption"
                        sx={{ whiteSpace: "pre-wrap", fontFamily: "monospace", m: 0 }}
                      >
                        {compactText(message.prompt_summary)}
                      </Typography>
                    ) : (
                      <Typography variant="caption" sx={{ color: "text.secondary" }}>
                        Not persisted for this path.
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ minWidth: 280, maxWidth: 420 }}>
                    {message.response_summary ? (
                      <Typography
                        component="pre"
                        variant="caption"
                        sx={{ whiteSpace: "pre-wrap", fontFamily: "monospace", m: 0 }}
                      >
                        {compactText(message.response_summary)}
                      </Typography>
                    ) : (
                      <Typography variant="caption" sx={{ color: "text.secondary" }}>
                        Not persisted for this path.
                      </Typography>
                    )}
                    {message.note ? (
                      <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.75 }}>
                        {message.note}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={message.status || "unknown"} />
                    {message.prompt_tokens || message.completion_tokens ? (
                      <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.5 }}>
                        {message.prompt_tokens || 0} in / {message.completion_tokens || 0} out
                      </Typography>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
              {!llmMessages.length && !loading ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      No LLM message records found.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </TableContainer>

        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Stack spacing={1.4}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 900, color: "#15243b" }}>
                Applied recommendation outcomes
              </Typography>
              <Typography variant="body2" sx={{ color: "#667085" }}>
                Last {appliedStats?.window_days || 30} days. Tracks the closed loop from recommendation to applied plan change.
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", rowGap: 1 }}>
              <Chip label={`Applied: ${appliedStats?.total_applied || 0}`} />
              <Chip label={`Undone: ${appliedStats?.undo_count || 0}`} />
              <Chip label={`Helpful: ${appliedStats?.helpful_count || 0}`} />
              <Chip label={`Not helpful: ${appliedStats?.not_helpful_count || 0}`} />
              <Chip label={`Measured: ${appliedStats?.measured_count || 0}`} />
              <Chip
                label={`Avg delta: ${
                  typeof appliedStats?.average_completion_rate_delta === "number"
                    ? `${appliedStats.average_completion_rate_delta >= 0 ? "+" : ""}${Math.round(appliedStats.average_completion_rate_delta * 100)}%`
                    : "n/a"
                }`}
              />
            </Stack>
            <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", rowGap: 1 }}>
              {Object.entries(appliedStats?.status_counts || {}).map(([status, count]) => (
                <Chip key={status} size="small" label={`${status}: ${count}`} />
              ))}
            </Stack>
          </Stack>
        </Paper>

        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Applied</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Change</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Outcome</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {appliedChanges.map((change) => (
                <TableRow key={change.id} hover>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{formatDate(change.applied_at)}</TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {change.username || `User ${change.user_id}`}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      ID {change.user_id}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 420 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {change.title || "Plan change"}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      {change.message}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={change.status_label || change.status || "Tracking"} />
                  </TableCell>
                  <TableCell>
                    <Stack spacing={0.2}>
                      <Typography variant="caption">
                        Before {typeof change.before_completion_rate === "number" ? `${Math.round(change.before_completion_rate * 100)}%` : "n/a"}
                      </Typography>
                      <Typography variant="caption">
                        After {typeof change.after_completion_rate === "number" ? `${Math.round(change.after_completion_rate * 100)}%` : "n/a"}
                      </Typography>
                      <Typography variant="caption">
                        Feedback {change.user_feedback || "none"}
                      </Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {!appliedChanges.length && !loading ? (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      No applied recommendations to show.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </TableContainer>

        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Time</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Event</TableCell>
                <TableCell>Metadata</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {events.map((event) => (
                <TableRow key={event.id} hover>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{formatDate(event.created_at)}</TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {event.username || `User ${event.user_id}`}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      ID {event.user_id}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={event.event_type} />
                  </TableCell>
                  <TableCell sx={{ maxWidth: 520 }}>
                    <Typography variant="caption" sx={{ fontFamily: "monospace", whiteSpace: "normal" }}>
                      {metadataSummary(event.metadata)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
              {!events.length && !loading ? (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      No events to show.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>
    </Box>
  );
}
